import { useState, useRef } from "react";
import { CrossPromo } from "../components/CrossPromo.jsx";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { HomeButton } from "../components/HomeButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

const INDIGO = "#6366F1";
const VIOLET = "#8B5CF6";
const INDIGO_LIGHT = "#A5B4FC";

// ─── File Extraction (100% browser-side, no API) ───
// Heavy parsing libraries (pdfjs, mammoth, jszip) are dynamically imported on first
// use so they don't bloat the initial bundle. A user who only pastes text will never
// download them.
const SUPPORTED_FORMATS = {
  ".pdf":  { label: "PDF",        method: "pdf"  },
  ".docx": { label: "Word",       method: "docx" },
  ".pptx": { label: "PowerPoint", method: "pptx" },
  ".txt":  { label: "Text",       method: "text" },
  ".md":   { label: "Markdown",   method: "text" },
  ".tex":  { label: "LaTeX",      method: "text" },
  ".rtf":  { label: "Rich Text",  method: "text" },
};
const ACCEPTED_EXTENSIONS = Object.keys(SUPPORTED_FORMATS).join(",");

// Helpers ─────────────────────────────────────────────────────────
const fileToArrayBuffer = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = () => reject(new Error("Failed to read file"));
  r.readAsArrayBuffer(file);
});

const fileToText = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = () => reject(new Error("Failed to read file"));
  r.readAsText(file);
});

// PDF extraction ──────────────────────────────────────────────────
// Uses Mozilla's pdf.js. The worker is bundled LOCALLY by Vite (no CDN)
// so PDF word-count works offline and keeps the project's no-CDN
// posture. Vite resolves the `?url` import to a hashed local asset.
async function extractPdf(file) {
  const pdfjs = await import("pdfjs-dist/build/pdf");
  const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const buffer = await fileToArrayBuffer(file);
  const pdf = await pdfjs.getDocument({ data: buffer }).promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    // Each item is a positioned text fragment; join with spaces and add newlines
    // where pdf.js indicates an end-of-line.
    let pageText = "";
    let lastY = null;
    for (const item of content.items) {
      if (lastY !== null && item.transform && item.transform[5] !== lastY) {
        pageText += "\n";
      }
      pageText += item.str + (item.hasEOL ? "\n" : " ");
      lastY = item.transform ? item.transform[5] : lastY;
    }
    pages.push(pageText.trim());
  }
  return pages.join("\n\n");
}

// DOCX extraction ─────────────────────────────────────────────────
// Uses mammoth.js — produces clean plain text from .docx files.
async function extractDocx(file) {
  const mammoth = await import("mammoth/mammoth.browser");
  const arrayBuffer = await fileToArrayBuffer(file);
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value || "";
}

// PPTX extraction ─────────────────────────────────────────────────
// A .pptx is just a zip containing XML. We unzip with JSZip and pull text
// out of <a:t> nodes in each slide's XML.
async function extractPptx(file) {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(file);

  // Slides are at ppt/slides/slide1.xml, slide2.xml, ...
  const slidePaths = Object.keys(zip.files)
    .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
    .sort((a, b) => {
      const n = (s) => parseInt(s.match(/slide(\d+)\.xml/)[1], 10);
      return n(a) - n(b);
    });

  const slides = [];
  for (const path of slidePaths) {
    const xml = await zip.files[path].async("string");
    // <a:t> tags contain the runs of visible text. This is a focused regex,
    // not a full XML parse — fine because the schema is stable and we only
    // want the text content.
    const matches = xml.match(/<a:t[^>]*>([\s\S]*?)<\/a:t>/g) || [];
    const text = matches
      .map((m) => m.replace(/<a:t[^>]*>|<\/a:t>/g, ""))
      // Decode the handful of XML entities pptx actually uses.
      .map((s) => s
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'"))
      .join(" ");
    if (text.trim()) slides.push(text.trim());
  }
  return slides.join("\n\n");
}

// Public entrypoint ───────────────────────────────────────────────
async function extractTextFromFile(file) {
  const ext = "." + file.name.split(".").pop().toLowerCase();
  const format = SUPPORTED_FORMATS[ext];
  if (!format) throw new Error("Unsupported file type");

  switch (format.method) {
    case "text": return fileToText(file);
    case "pdf":  return extractPdf(file);
    case "docx": return extractDocx(file);
    case "pptx": return extractPptx(file);
    default:     throw new Error("Unsupported file type");
  }
}

function countSyllables(word) {
  word = word.toLowerCase().replace(/[^a-z]/g, "");
  if (word.length <= 3) return 1;
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "");
  word = word.replace(/^y/, "");
  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
}

function analyze(text) {
  if (!text.trim()) {
    return { words: 0, chars: 0, charsNoSpace: 0, sentences: 0, paragraphs: 0, syllables: 0, readingTime: "0 sec", speakingTime: "0 sec", fleschScore: 0, fleschGrade: "—", gradeLevel: "—", pages: "0", pagesDouble: "0" };
  }

  const chars = text.length;
  const charsNoSpace = text.replace(/\s/g, "").length;
  const words = text.split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length || 1;
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0).length || 1;

  let totalSyllables = 0;
  words.forEach(w => { totalSyllables += countSyllables(w); });

  // Reading time (238 wpm avg) and speaking time (150 wpm avg)
  const readMin = wordCount / 238;
  const speakMin = wordCount / 150;
  const readingTime = readMin < 1 ? `${Math.max(1, Math.round(readMin * 60))} sec` : `${Math.round(readMin)} min`;
  const speakingTime = speakMin < 1 ? `${Math.max(1, Math.round(speakMin * 60))} sec` : `${Math.round(speakMin)} min`;

  // Flesch Reading Ease
  const avgWordsPerSentence = wordCount / sentences;
  const avgSyllablesPerWord = totalSyllables / wordCount;
  const fleschScore = Math.round(206.835 - 1.015 * avgWordsPerSentence - 84.6 * avgSyllablesPerWord);

  let fleschGrade = "—";
  if (fleschScore >= 90) fleschGrade = "Very Easy";
  else if (fleschScore >= 80) fleschGrade = "Easy";
  else if (fleschScore >= 70) fleschGrade = "Fairly Easy";
  else if (fleschScore >= 60) fleschGrade = "Standard";
  else if (fleschScore >= 50) fleschGrade = "Fairly Hard";
  else if (fleschScore >= 30) fleschGrade = "Hard";
  else fleschGrade = "Very Hard";

  // Flesch-Kincaid Grade Level
  const fkGrade = 0.39 * avgWordsPerSentence + 11.8 * avgSyllablesPerWord - 15.59;
  const gradeLevel = fkGrade < 1 ? "K" : fkGrade > 16 ? "16+" : Math.round(fkGrade).toString();

  // Page estimates (250 words/page single, 125 double)
  const pages = formatNum(wordCount / 250, 1);
  const pagesDouble = formatNum(wordCount / 125, 1);

  return { words: wordCount, chars, charsNoSpace, sentences, paragraphs, syllables: totalSyllables, readingTime, speakingTime, fleschScore: Math.max(0, Math.min(100, fleschScore)), fleschGrade, gradeLevel, pages, pagesDouble };
}

function StatBox({ label, value, sub }) {
  return (
    <div style={{
      padding: "14px 12px", borderRadius: 12, textAlign: "center",
      background: rgba(INDIGO, 0.04), border: `1px solid ${rgba(INDIGO, 0.08)}`,
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: INDIGO_LIGHT, letterSpacing: "-0.5px" }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: "#4B5563", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ReadabilityMeter({ score, grade }) {
  const clamp = Math.max(0, Math.min(100, score));
  const color = clamp >= 70 ? "#10B981" : clamp >= 50 ? "#FBBF24" : "#F43F5E";
  return (
    <div style={{
      padding: "18px 16px", borderRadius: 14,
      background: rgba(INDIGO, 0.04), border: `1px solid ${rgba(INDIGO, 0.08)}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Readability</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{grade}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.04)", overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 4, transition: "width 0.4s ease, background 0.3s ease",
          width: `${clamp}%`, background: color,
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: "#4B5563" }}>
        <span>Hard</span>
        <span>Flesch: {score}/100</span>
        <span>Easy</span>
      </div>
    </div>
  );
}

export default function WordCounterApp() {
  const [text, setText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);
  const stats = analyze(text);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!SUPPORTED_FORMATS[ext]) { alert("Unsupported file type."); return; }
    setExtracting(true); setUploadedFile(file.name);
    try {
      const extracted = await extractTextFromFile(file);
      setText(prev => prev ? prev + "\n\n" + extracted : extracted);
    } catch (e) { console.error(e); alert("Failed to extract text."); setUploadedFile(null); }
    setExtracting(false);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer?.files?.[0]); };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", right: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(INDIGO, 0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />

      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(INDIGO, 0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HomeButton accentColor={INDIGO} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${INDIGO}, ${VIOLET})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>W</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${INDIGO_LIGHT}, #C4B5FD)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>WordCount</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdStack</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 38, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: "-1px", background: `linear-gradient(135deg, #E0E7FF, ${INDIGO_LIGHT} 40%, #C4B5FD 70%, #DDD6FE)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Word and Character Counter
          </h1>
          <p style={{ fontSize: 15, color: "#6B7280", margin: 0 }}>Paste text or drop a document. Get instant word count, readability, and page estimates.</p>
        </div>

        {/* File drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
          onClick={() => !extracting && fileInputRef.current?.click()}
          style={{
            borderRadius: 14, padding: 20, marginBottom: 14, cursor: extracting ? "wait" : "pointer",
            border: `1.5px dashed ${dragOver ? rgba(INDIGO, 0.5) : rgba(INDIGO, 0.1)}`,
            background: dragOver ? rgba(INDIGO, 0.05) : "transparent",
            transition: "all 0.3s ease",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}>
          <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS} style={{ display: "none" }}
            onChange={e => handleFile(e.target.files?.[0])} />
          {extracting ? (
            <span style={{ fontSize: 14, color: INDIGO_LIGHT }}>Extracting text from {uploadedFile}...</span>
          ) : uploadedFile ? (
            <span style={{ fontSize: 14, color: "#6EE7B7" }}>Loaded: {uploadedFile} <span style={{ color: "#4B5563", marginLeft: 8 }}>Drop another to replace</span></span>
          ) : (
            <span style={{ fontSize: 13, color: "#4B5563" }}>
              📄 Drop a file (PDF, Word, PPTX, LaTeX, RTF, TXT) or <span style={{ color: "#6B7280", textDecoration: "underline" }}>click to upload</span>
            </span>
          )}
        </div>

        {/* Text input */}
        <div style={{
          borderRadius: 18, padding: 2,
          border: `1px solid ${rgba(INDIGO, 0.1)}`,
          background: "rgba(10,11,20,0.6)",
          boxShadow: "0 4px 30px rgba(0,0,0,0.2)",
          marginBottom: 24,
        }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Paste or type your essay, paper, or any text here..."
            style={{
              width: "100%", minHeight: 200, padding: 22, borderRadius: 16,
              background: "transparent", border: "none", resize: "vertical",
              color: "#E2E8F0", fontSize: 15, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              lineHeight: 1.7, outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Primary stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
          <StatBox label="Words" value={stats.words} />
          <StatBox label="Characters" value={stats.chars} />
          <StatBox label="No Spaces" value={stats.charsNoSpace} />
          <StatBox label="Sentences" value={stats.sentences} />
          <StatBox label="Paragraphs" value={stats.paragraphs} />
        </div>

        {/* Time and pages */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <StatBox label="Reading Time" value={stats.readingTime} sub="238 wpm" />
          <StatBox label="Speaking Time" value={stats.speakingTime} sub="150 wpm" />
          <StatBox label="Pages (1x)" value={stats.pages} sub="single spaced" />
          <StatBox label="Pages (2x)" value={stats.pagesDouble} sub="double spaced" />
        </div>

        {/* Readability */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <ReadabilityMeter score={stats.fleschScore} grade={stats.fleschGrade} />
          <div style={{
            padding: "18px 16px", borderRadius: 14,
            background: rgba(INDIGO, 0.04), border: `1px solid ${rgba(INDIGO, 0.08)}`,
            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Grade Level</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: INDIGO_LIGHT }}>{stats.gradeLevel}</div>
            <div style={{ fontSize: 11, color: "#4B5563", marginTop: 4 }}>Flesch-Kincaid</div>
          </div>
        </div>

        {/* Common targets reference */}
        <div style={{
          padding: "18px 20px", borderRadius: 14,
          background: rgba(INDIGO, 0.03), border: `1px solid ${rgba(INDIGO, 0.06)}`,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Common Essay Lengths</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "6px 24px", fontSize: 13 }}>
            {[
              ["High school essay", "300–1,000 words"],
              ["College essay", "1,500–5,000 words"],
              ["Common App essay", "250–650 words"],
              ["Graduate thesis", "10,000–50,000 words"],
              ["Blog post", "1,000–2,500 words"],
              ["Short answer", "100–400 words"],
            ].map(([label, range], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span style={{ color: "#6B7280" }}>{label}</span>
                <span style={{ color: INDIGO_LIGHT, fontWeight: 600 }}>{range}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Info sections for SEO */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 32 }}>
          {[
            { icon: "📝", title: "Instant Count", text: "Words, characters, sentences, and paragraphs update in real time as you type." },
            { icon: "📖", title: "Readability", text: "Flesch Reading Ease score and grade level help you write for your audience." },
            { icon: "📄", title: "Page Estimates", text: "See how many pages your text fills, single or double spaced, at standard formatting." },
          ].map((item, i) => (
            <div key={i} style={{ padding: "18px 14px", borderRadius: 14, background: rgba(INDIGO, 0.02), border: `1px solid ${rgba(INDIGO, 0.05)}` }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: INDIGO_LIGHT, marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>{item.text}</div>
            </div>
          ))}
        </div>

        {/* Cross promo */}
        <div style={{ marginTop: 32, padding: "20px", borderRadius: 16, background: `linear-gradient(135deg, ${rgba(INDIGO, 0.06)}, ${rgba(VIOLET, 0.06)})`, border: `1px solid ${rgba(INDIGO, 0.12)}`, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INDIGO_LIGHT, marginBottom: 6 }}>Writing an essay? Study smarter with NerdStack</div>
          {/* Cross-promo (rotates per page load) */}
          <CrossPromo currentSlug="wordcount" accent={INDIGO} accent2={VIOLET} accentLight={INDIGO_LIGHT} />
        </div>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(INDIGO, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        textarea::placeholder { color: #374151; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
